import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin, Clock, Instagram, Facebook } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";
import Reveal from "@/components/Reveal";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your enquiry. Please try calling us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="relative bg-[#080A0B] py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-16"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
        <Reveal className="lg:col-span-7">
          <div className="flex items-center gap-4 mb-8">
            <span className="block w-10 h-px bg-[#3FB8DC]" />
            <span className="eyebrow">Begin a project</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#F4F0EB] leading-[1.02] tracking-tight">
            Get in <span className="text-[#3FB8DC]">Touch.</span>
          </h2>
          <p className="mt-6 text-[#A1A1AA] max-w-xl leading-relaxed">
            Tell us about your project, plumbing issue or maintenance requirements
            and we'll be in touch to discuss the best solution.
          </p>
          <p className="mt-4 text-[#A1A1AA] max-w-xl leading-relaxed">
            Every enquiry is reviewed personally, and we aim to respond within
            one business day. For urgent plumbing issues, please call us directly.
          </p>

          <form onSubmit={submit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <div className="md:col-span-1">
              <label className="eyebrow block mb-1" style={{ fontSize: "0.6rem" }}>Your Name</label>
              <input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="luxe-input"
                placeholder="Eleanor Whitfield"
                required
              />
            </div>
            <div className="md:col-span-1">
              <label className="eyebrow block mb-1" style={{ fontSize: "0.6rem" }}>Email</label>
              <input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="luxe-input"
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="md:col-span-1 mt-6">
              <label className="eyebrow block mb-1" style={{ fontSize: "0.6rem" }}>Phone</label>
              <input
                data-testid="contact-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="luxe-input"
                placeholder="0414 000 000"
              />
            </div>
            <div className="md:col-span-1 mt-6">
              <label className="eyebrow block mb-1" style={{ fontSize: "0.6rem" }}>Service</label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger
                  data-testid="contact-service"
                  className="luxe-input flex justify-between !h-auto !rounded-none !border-0 !border-b !border-[#27272A] focus:!border-[#3FB8DC] focus:!ring-0 !bg-transparent text-[#F4F0EB] !pl-0"
                >
                  <SelectValue placeholder="Select an area of work" />
                </SelectTrigger>
                <SelectContent className="bg-[#0E1213] border-[#27272A] text-[#F4F0EB] rounded-none">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title} className="focus:bg-[#121618] focus:text-[#3FB8DC]">
                      {s.title}
                    </SelectItem>
                  ))}
                  <SelectItem value="Other / not sure" className="focus:bg-[#121618] focus:text-[#3FB8DC]">
                    Other / not sure
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 mt-6">
              <label className="eyebrow block mb-1" style={{ fontSize: "0.6rem" }}>Tell us about the project</label>
              <textarea
                data-testid="contact-message"
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="luxe-input resize-none"
                placeholder="A few lines on the property, the scope, and your timeline..."
                required
              />
            </div>
            <div className="md:col-span-2 mt-10">
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="btn-primary disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Enquiry"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </Reveal>

        <Reveal delay={180} className="lg:col-span-5">
          <div className="bg-[#0E1213] border border-[#27272A] p-8 md:p-10">
            <span className="eyebrow">Direct contact</span>
            <div className="mt-6 space-y-6">
              <a
                href={SITE.phoneHref}
                data-testid="contact-phone-link"
                className="flex items-start gap-4 group"
              >
                <Phone className="w-5 h-5 text-[#3FB8DC] mt-1 flex-shrink-0" />
                <div>
                  <div className="eyebrow" style={{ fontSize: "0.55rem" }}>Phone</div>
                  <div className="font-serif-display text-2xl text-[#F4F0EB] group-hover:text-[#3FB8DC] transition-colors">
                    {SITE.phone}
                  </div>
                </div>
              </a>
              <a
                href={SITE.emailHref}
                data-testid="contact-email-link"
                className="flex items-start gap-4 group"
              >
                <Mail className="w-5 h-5 text-[#3FB8DC] mt-1 flex-shrink-0" />
                <div>
                  <div className="eyebrow" style={{ fontSize: "0.55rem" }}>Email</div>
                  <div className="text-[#F4F0EB] group-hover:text-[#3FB8DC] transition-colors">
                    {SITE.email}
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#3FB8DC] mt-1 flex-shrink-0" />
                <div>
                  <div className="eyebrow" style={{ fontSize: "0.55rem" }}>Studio</div>
                  <div className="text-[#F4F0EB]">
                    {SITE.suburb}, {SITE.state}
                    <br />
                    <span className="text-[#A1A1AA] text-sm">Serving {SITE.region} & beyond</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="dotted-rule my-8" />

            <div className="flex items-start gap-4">
              <Instagram className="w-5 h-5 text-[#3FB8DC] mt-1 flex-shrink-0" />
              <div>
                <div className="eyebrow" style={{ fontSize: "0.55rem" }}>Follow</div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1">
                  <a
                    href={SITE.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram"
                    className="text-[#F4F0EB] hover:text-[#3FB8DC] transition-colors text-sm"
                  >
                    {SITE.instagramHandle}
                  </a>
                  <a
                    href={SITE.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-facebook"
                    className="text-[#F4F0EB] hover:text-[#3FB8DC] transition-colors text-sm inline-flex items-center gap-1.5"
                  >
                    <Facebook className="w-3.5 h-3.5" /> {SITE.facebookHandle}
                  </a>
                </div>
              </div>
            </div>

            <div className="dotted-rule my-8" />

            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-[#3FB8DC] mt-1 flex-shrink-0" />
              <div className="w-full">
                <div className="eyebrow mb-3" style={{ fontSize: "0.55rem" }}>Hours</div>
                <ul className="space-y-2 text-sm">
                  {SITE.hours.map((h) => (
                    <li key={h.day} className="flex justify-between text-[#F4F0EB]/90">
                      <span>{h.day}</span>
                      <span className="text-[#A1A1AA]">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-8 p-8 bg-[#3FB8DC] text-[#080A0B]">
            <span className="eyebrow !text-[#080A0B]" style={{ fontSize: "0.6rem" }}>
              Emergency callout
            </span>
            <h3 className="font-serif-display text-3xl mt-4 leading-tight">
              Burst pipe? Blocked drain? No hot water?
            </h3>
            <p className="text-sm mt-3 mb-6 opacity-90">
              Our after-hours line is monitored seven days. Call directly — we'll triage on the spot.
            </p>
            <a
              href={SITE.phoneHref}
              data-testid="contact-emergency-call"
              className="inline-flex items-center gap-2 border border-[#080A0B] px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#080A0B] hover:text-[#3FB8DC] transition-colors"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
